import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  Plus, Edit2, Trash2, Save, X, Copy, EyeOff, Eye, TrendingUp,
  ArrowLeft, DollarSign, Package, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = [
  'Consultation & Documentation',
  'Travel & Logistics',
  'Health & Compliance',
  'Accommodation',
];

const EMPTY_ITEM = {
  category: 'Consultation & Documentation',
  name: '',
  description: '',
  bundleNote: '',
  note: '',
  pricing: { base: '' },
  includes: [],
  admin_presets: [],
  order: 0,
  is_published: true,
};

export default function PriceListManager() {
  const [password] = useState(sessionStorage.getItem('admin_password') || '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showBulk, setShowBulk] = useState(false);

  const authHeader = () => ({ 'Authorization': `Basic ${btoa(':' + password)}` });

  const fetchItems = async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API_URL}/api/admin/price-list`, { headers: authHeader() });
      const d = await r.json();
      setItems(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSave = async (item) => {
    try {
      const isEdit = Boolean(item.id);
      const url = isEdit
        ? `${API_URL}/api/admin/price-list/${item.id}`
        : `${API_URL}/api/admin/price-list`;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(item),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setEditing(null);
      fetchItems();
    } catch (e) { alert(`Save failed: ${e.message}`); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    await fetch(`${API_URL}/api/admin/price-list/${item.id}`, {
      method: 'DELETE', headers: authHeader(),
    });
    fetchItems();
  };

  const handleDuplicate = async (item) => {
    await fetch(`${API_URL}/api/admin/price-list/${item.id}/duplicate`, {
      method: 'POST', headers: authHeader(),
    });
    fetchItems();
  };

  const handleTogglePublish = async (item) => {
    await handleSave({ ...item, is_published: !item.is_published });
  };

  const byCategory = {};
  items.forEach(i => {
    (byCategory[i.category] ||= []).push(i);
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                Price List Manager
              </h1>
              <p className="text-xs text-gray-500">Edit once — changes appear on the public site and every quote/invoice/receipt form.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBulk(true)}
              data-testid="bulk-adjust-btn"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Bulk Adjust
            </Button>
            <Button
              onClick={() => setEditing({ ...EMPTY_ITEM })}
              data-testid="add-price-item-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading && <p className="text-gray-500">Loading…</p>}

        {CATEGORIES.map(cat => {
          const rows = byCategory[cat] || [];
          if (!rows.length) return null;
          return (
            <div key={cat} className="mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-emerald-600" />
                {cat}
                <span className="text-sm text-gray-400 font-normal">({rows.length})</span>
              </h2>
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-left text-xs uppercase text-gray-500">
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Pricing</th>
                      <th className="px-4 py-3">Presets</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(item => (
                      <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500 line-clamp-1">{item.description}</div>
                          {item.bundleNote && (
                            <div className="text-xs text-orange-600 mt-1 font-medium line-clamp-1">🎁 {item.bundleNote}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-700">
                          {Object.entries(item.pricing || {}).map(([k, v]) => (
                            <div key={k}><span className="text-gray-400">{k}:</span> {v}</div>
                          ))}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{(item.admin_presets || []).length} row(s)</td>
                        <td className="px-4 py-3">
                          {item.is_published ? (
                            <span className="text-[10px] uppercase font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Live</span>
                          ) : (
                            <span className="text-[10px] uppercase font-semibold px-2 py-1 rounded bg-gray-100 text-gray-600 border border-gray-300">Draft</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => handleTogglePublish(item)} title={item.is_published ? 'Un-publish' : 'Publish'} className="p-2 text-gray-500 hover:text-gray-800" data-testid={`toggle-pub-${item.id}`}>
                            {item.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDuplicate(item)} title="Duplicate" className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                            <Copy className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditing({ ...item })} title="Edit" className="p-2 text-emerald-700 hover:bg-emerald-50 rounded" data-testid={`edit-price-${item.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(item)} title="Delete" className="p-2 text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <EditItemModal item={editing} onSave={handleSave} onClose={() => setEditing(null)} />
      )}
      {showBulk && (
        <BulkAdjustModal password={password} onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); fetchItems(); }} />
      )}
    </div>
  );
}


function EditItemModal({ item, onSave, onClose }) {
  const [form, setForm] = useState({
    ...item,
    pricing: item.pricing || {},
    includes: item.includes || [],
    admin_presets: item.admin_presets || [],
  });

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPricing = (k, v) => setForm(f => ({ ...f, pricing: { ...f.pricing, [k]: v } }));
  const removePricingKey = (k) => setForm(f => {
    const next = { ...f.pricing }; delete next[k]; return { ...f, pricing: next };
  });
  const [newPKey, setNewPKey] = useState('');
  const [newPVal, setNewPVal] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{item.id ? 'Edit Service' : 'Add Service'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Category</label>
              <select className="border rounded w-full px-3 py-2 mt-1" value={form.category} onChange={e => setField('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Order</label>
              <input type="number" className="border rounded w-full px-3 py-2 mt-1" value={form.order} onChange={e => setField('order', parseInt(e.target.value) || 0)} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Name</label>
            <input className="border rounded w-full px-3 py-2 mt-1" value={form.name} onChange={e => setField('name', e.target.value)} data-testid="price-name-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Description (tagline)</label>
            <textarea className="border rounded w-full px-3 py-2 mt-1" rows="2" value={form.description || ''} onChange={e => setField('description', e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-semibold text-orange-700 flex items-center gap-1">
              <Info className="w-3 h-3" /> Bundle callout (orange) — optional
            </label>
            <input className="border rounded w-full px-3 py-2 mt-1" placeholder="e.g. Bundle available — pair with X for $Y (save $Z)" value={form.bundleNote || ''} onChange={e => setField('bundleNote', e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-semibold text-amber-700 flex items-center gap-1">
              <Info className="w-3 h-3" /> Amber note (pricing caveat) — optional
            </label>
            <input className="border rounded w-full px-3 py-2 mt-1" placeholder="e.g. Save $25 vs booking separately" value={form.note || ''} onChange={e => setField('note', e.target.value)} />
          </div>

          {/* Pricing tiers */}
          <div className="border-t pt-4">
            <label className="text-xs font-semibold text-gray-600">Pricing tiers</label>
            <p className="text-xs text-gray-400 mb-2">Common keys: <code>base</code>, <code>firstPet</code>, <code>firstAdditional</code>, <code>secondAdditional</code>, <code>additional</code>, <code>homeVisit</code></p>
            {Object.entries(form.pricing).map(([k, v]) => (
              <div key={k} className="flex gap-2 mt-2">
                <input className="border rounded px-2 py-1 text-sm w-40 bg-gray-50" value={k} readOnly />
                <input className="border rounded px-2 py-1 text-sm flex-1" value={v} onChange={e => setPricing(k, e.target.value)} placeholder='e.g. "$350"' />
                <button onClick={() => removePricingKey(k)} className="text-red-600 px-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="flex gap-2 mt-3 items-end">
              <input className="border rounded px-2 py-1 text-sm w-40" placeholder="new key" value={newPKey} onChange={e => setNewPKey(e.target.value)} />
              <input className="border rounded px-2 py-1 text-sm flex-1" placeholder='value e.g. "$50"' value={newPVal} onChange={e => setNewPVal(e.target.value)} />
              <Button size="sm" variant="outline" onClick={() => { if (newPKey && newPVal) { setPricing(newPKey, newPVal); setNewPKey(''); setNewPVal(''); } }}>
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Includes */}
          <div className="border-t pt-4">
            <label className="text-xs font-semibold text-gray-600">What's Included bullets</label>
            {form.includes.map((b, i) => (
              <div key={i} className="flex gap-2 mt-2">
                <input className="border rounded px-2 py-1 text-sm flex-1" value={b} onChange={e => {
                  const next = [...form.includes]; next[i] = e.target.value; setField('includes', next);
                }} />
                <button onClick={() => setField('includes', form.includes.filter((_, j) => j !== i))} className="text-red-600 px-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setField('includes', [...form.includes, ''])}>
              <Plus className="w-3 h-3 mr-1" /> Add bullet
            </Button>
          </div>

          {/* Admin presets */}
          <div className="border-t pt-4">
            <label className="text-xs font-semibold text-gray-600">Admin form presets</label>
            <p className="text-xs text-gray-400 mb-2">These rows appear in the Quote / Invoice / Receipt line-item preset dropdowns.</p>
            {form.admin_presets.map((p, i) => (
              <div key={i} className="flex gap-2 mt-2">
                <input className="border rounded px-2 py-1 text-sm flex-1" placeholder="label" value={p.label} onChange={e => {
                  const next = [...form.admin_presets]; next[i] = { ...p, label: e.target.value }; setField('admin_presets', next);
                }} />
                <input type="number" className="border rounded px-2 py-1 text-sm w-24" placeholder="price" value={p.price} onChange={e => {
                  const next = [...form.admin_presets]; next[i] = { ...p, price: parseFloat(e.target.value) || 0 }; setField('admin_presets', next);
                }} />
                <input className="border rounded px-2 py-1 text-sm w-48" placeholder="note (optional)" value={p.note || ''} onChange={e => {
                  const next = [...form.admin_presets]; next[i] = { ...p, note: e.target.value }; setField('admin_presets', next);
                }} />
                <button onClick={() => setField('admin_presets', form.admin_presets.filter((_, j) => j !== i))} className="text-red-600 px-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setField('admin_presets', [...form.admin_presets, { label: '', price: 0, note: '' }])}>
              <Plus className="w-3 h-3 mr-1" /> Add preset row
            </Button>
          </div>

          <div className="border-t pt-4 flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_published} onChange={e => setField('is_published', e.target.checked)} />
              Published (visible on public site)
            </label>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose}><X className="w-4 h-4 mr-2" /> Cancel</Button>
          <Button onClick={() => onSave(form)} data-testid="save-price-item-btn"><Save className="w-4 h-4 mr-2" /> Save</Button>
        </div>
      </div>
    </div>
  );
}


function BulkAdjustModal({ password, onClose, onDone }) {
  const [category, setCategory] = useState('');
  const [percent, setPercent] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const authHeader = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Basic ${btoa(':' + password)}`,
  });

  const run = async (dry) => {
    if (!percent) return alert('Enter a percentage.');
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/price-list/bulk-adjust`, {
        method: 'POST', headers: authHeader(),
        body: JSON.stringify({ category: category || null, percent: parseFloat(percent), dry_run: dry }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      if (dry) { setPreview(d); }
      else { alert(`Applied — ${d.count_changed} service(s) updated.`); onDone(); }
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-600" /> Bulk Price Adjustment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Category (leave blank for ALL)</label>
              <select className="border rounded w-full px-3 py-2 mt-1" value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">All categories</option>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Percentage change</label>
              <input type="number" className="border rounded w-full px-3 py-2 mt-1" placeholder="e.g. 5 for +5%, -10 for -10%" value={percent} onChange={e => setPercent(e.target.value)} data-testid="bulk-percent-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="bulk-preview-btn">Preview</Button>
            <Button onClick={() => run(false)} disabled={busy || !preview} data-testid="bulk-apply-btn" className="bg-emerald-600 hover:bg-emerald-700">Apply Changes</Button>
          </div>
          {preview && (
            <div className="border-t pt-4">
              <p className="text-sm font-semibold mb-2">{preview.count_changed} service(s) would be updated:</p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {preview.changes.map(c => (
                  <div key={c.id} className="text-xs border rounded p-2">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-red-600">Before: {JSON.stringify(c.before.pricing)}</div>
                    <div className="text-emerald-700">After: {JSON.stringify(c.after.pricing)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
